/**
 * Theme configuration for the NH Mesh Live Dashboard.
 * Defines colors, typography, spacing, and other design tokens.
 * Used with styled-components ThemeProvider.
 */
export const theme = {
  colors: {
    // Primary palette
    primary: '#3498db',
    primaryLight: '#5dade2',
    primaryDark: '#2980b9',
    
    // Secondary palette
    secondary: '#2ecc71',
    secondaryLight: '#58d68d',
    secondaryDark: '#27ae60',
    
    // Status colors
    success: '#2ecc71',
    warning: '#f39c12',
    danger: '#e74c3c',
    info: '#3498db',
    
    // Node status colors
    nodeOnline: '#2ecc71',
    nodeOffline: '#95a5a6',
    nodeLowBattery: '#f39c12',
    
    // Connection quality colors
    connectionExcellent: '#2ecc71',
    connectionGood: '#27ae60',
    connectionFair: '#f39c12',
    connectionPoor: '#e74c3c',
    
    // Neutral palette
    background: '#f8f9fa',
    backgroundDark: '#e9ecef',
    backgroundLight: '#ffffff',
    text: '#2c3e50',
    textLight: '#7f8c8d',
    textDark: '#1a252f',
    border: '#dee2e6',
    borderLight: '#e9ecef',
    borderDark: '#ced4da',
  },
  
  // Typography
  fonts: {
    body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
    heading: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
    monospace: "SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  
  fontSizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    md: '1rem',       // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem',    // 48px
  },
  
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  
  // Spacing
  space: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    '2xl': '2.5rem',  // 40px
    '3xl': '3rem',    // 48px
  },
  
  // Borders
  radii: {
    sm: '0.125rem',   // 2px
    md: '0.25rem',    // 4px
    lg: '0.5rem',     // 8px
    xl: '1rem',       // 16px
    full: '9999px',
  },
  
  // Shadows
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
  },
  
  // Transitions
  transitions: {
    fast: '150ms ease-in-out',
    normal: '300ms ease-in-out',
    slow: '500ms ease-in-out',
  },
  
  // Z-index
  zIndices: {
    base: 0,
    overlay: 10,
    dropdown: 1000,
    sticky: 1100,
    fixed: 1200,
    modal: 1300,
    popover: 1400,
    tooltip: 1500,
  },
};

// Type definition for the theme
export type Theme = typeof theme;

// Type for styled-components to use our theme
declare module 'styled-components' {
  export interface DefaultTheme extends Theme {}
} 